/**
 * Money handling for /sale/quote and /purchases.
 *
 * Backend ships micro-USDC (6 decimals) as JSON numbers. We convert to
 * bigint immediately so any downstream arithmetic (approve amount,
 * maxTotalPrice) stays integer-safe. Display formatting is a pure
 * projection — the operative value stays bigint.
 */

/**
 * Coerce a wire JSON value into a bigint. Accepts number, string, bigint.
 * Rejects non-integers, non-finite, negative, empty, malformed input.
 */
export function toBigInt(v: unknown): bigint {
  if (typeof v === "bigint") return v;
  if (typeof v === "number") {
    if (!Number.isFinite(v) || !Number.isInteger(v)) {
      throw new Error(`toBigInt: non-integer number ${v}`);
    }
    return BigInt(v);
  }
  if (typeof v === "string") {
    const s = v.trim();
    if (!/^-?\d+$/.test(s)) {
      throw new Error(`toBigInt: malformed string "${v}"`);
    }
    return BigInt(s);
  }
  throw new Error(`toBigInt: unsupported type ${typeof v}`);
}

/**
 * Format a micro-USDC bigint (6 decimals) for display.
 *
 * Examples:
 *   formatMicrousdc(0n)             → "0.00 USDC"
 *   formatMicrousdc(1n)             → "0.000001 USDC"
 *   formatMicrousdc(2_900_000n)     → "2.90 USDC"
 *   formatMicrousdc(1_000_000_000n) → "1000.00 USDC"
 *
 * Never emits floating-point arithmetic. Uses integer division on the
 * bigint to split the whole and fractional parts.
 */
export function formatMicrousdc(
  micro: bigint,
  opts: { minFractionDigits?: number; symbol?: string } = {},
): string {
  const minFrac = Math.max(0, Math.min(6, opts.minFractionDigits ?? 2));
  const symbol = opts.symbol ?? "USDC";
  const isNeg = micro < 0n;
  const abs = isNeg ? -micro : micro;
  const whole = abs / 1_000_000n;
  const frac = abs % 1_000_000n;
  // Full 6-digit fractional; then trim trailing zeros down to minFrac.
  const fracStr = frac.toString().padStart(6, "0");
  let trimmed = fracStr.replace(/0+$/, "");
  if (trimmed.length < minFrac) {
    trimmed = fracStr.slice(0, minFrac);
  }
  const sign = isNeg ? "-" : "";
  return trimmed.length > 0
    ? `${sign}${whole.toString()}.${trimmed} ${symbol}`.trim()
    : `${sign}${whole.toString()} ${symbol}`.trim();
}

/** Multiply a micro-USDC bigint by an integer slot count safely. */
export function multiplyMicrousdc(unit: bigint, slots: number): bigint {
  if (!Number.isInteger(slots) || slots < 0) {
    throw new Error(`multiplyMicrousdc: invalid slots ${slots}`);
  }
  return unit * BigInt(slots);
}
